import { useState, useId } from 'react';
import { useTranslate } from 'nucleus-text';
import type { FileRejection, FileError } from 'react-dropzone';
import { v4 as uuid } from 'uuid';
import FormElement from '@cvent/carina/components/FormElement/FormElement';
import { FileType } from '@cvent/carina/components/legacy/FileUpload/FileUpload';
import FileUpload from '@cvent/carina/components/FileUpload/FileUpload';
import Button from '@cvent/carina/components/Button';

export const errorTypes = {
  FILE_NUM_EXCEEDS_LIMIT: 'file-num-exceeds-limit',
  TOO_MANY_FILES: 'too-many-files',
  FILE_TOO_LARGE: 'file-too-large',
  FILE_INVALID_TYPE: 'file-invalid-type'
};

const maxFileSizeInBytes = 10 * 1024 * 1024; // max file size should be 10MB in bytes
const acceptedFileTypes = ['pdf', 'doc', 'docx', 'txt', 'ppt', 'pptx', 'jpg', 'jpeg', 'png', 'xls', 'xlsx', 'csv']; // Supported file types for file upload component
type Files = {
  id: string;
  name: string;
  size: number;
  type: string;
  lastModified: number;
};
type ExtendedFileRejection = (
  | FileRejection
  | {
      error: FileError;
      file: FileType;
    }
)[];

type Alert = {
  type: string;
  files: string[];
};

type ApptFileUploadProps = {
  setAlerts: React.Dispatch<React.SetStateAction<Alert[]>>;
};

export function ApptFileUpload({ setAlerts }: ApptFileUploadProps): JSX.Element {
  const id = useId();
  const { translate } = useTranslate();

  const [showFileUpload, setShowFileUpload] = useState(false);

  const [files, setFiles] = useState<Files[]>([]);

  const onSelected = (acceptedFiles: FileType[], rejectedFiles: ExtendedFileRejection) => {
    const newFilesWithUUID = acceptedFiles.map((file: File) => ({
      id: uuid(),
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    }));

    const fileNumExceedsLimit = rejectedFiles.filter(
      fileRejection => 'error' in fileRejection && fileRejection.error?.code === errorTypes.FILE_NUM_EXCEEDS_LIMIT
    );
    const tooManyFiles = rejectedFiles.filter(
      fileRejection =>
        'errors' in fileRejection && fileRejection.errors?.some(error => error.code === errorTypes.TOO_MANY_FILES)
    );
    const fileTooLarge = rejectedFiles.filter(
      fileRejection =>
        'errors' in fileRejection && fileRejection.errors?.some(error => error.code === errorTypes.FILE_TOO_LARGE)
    );
    const invalidFileType = rejectedFiles.filter(
      fileRejection =>
        'errors' in fileRejection && fileRejection.errors?.some(error => error.code === errorTypes.FILE_INVALID_TYPE)
    );

    if (tooManyFiles.length > 0) {
      const tooManyFilesNames = tooManyFiles.map(fileRejection => fileRejection.file.name);
      setAlerts(prevAlerts => [...prevAlerts, { type: errorTypes.TOO_MANY_FILES, files: tooManyFilesNames }]);
    }

    if (fileTooLarge.length > 0) {
      const fileTooLargeNames = fileTooLarge.map(fileRejection => fileRejection.file.name);
      setAlerts(prevAlerts => [...prevAlerts, { type: errorTypes.FILE_TOO_LARGE, files: fileTooLargeNames }]);
    }

    if (invalidFileType.length > 0) {
      const invalidFileTypeNames = invalidFileType.map(fileRejection => fileRejection.file.name);
      setAlerts(prevAlerts => [...prevAlerts, { type: errorTypes.FILE_INVALID_TYPE, files: invalidFileTypeNames }]);
    }

    if (fileNumExceedsLimit.length > 0) {
      const fileNumExceedsLimitNames = fileNumExceedsLimit.map(fileRejection => fileRejection.file.name);
      setAlerts(prevAlerts => [
        ...prevAlerts,
        { type: errorTypes.FILE_NUM_EXCEEDS_LIMIT, files: fileNumExceedsLimitNames }
      ]);
    }

    setFiles(oldFiles => [...oldFiles, ...newFilesWithUUID]);
  };

  const onRemove = (fileToRemove: Files) => {
    setFiles(allFiles => allFiles.filter(item => item.id !== fileToRemove.id));
  };

  return !showFileUpload ? (
    <FormElement css={{ paddingTop: 8 }}>
      <Button onClick={() => setShowFileUpload(true)} text={translate('AppointmentPanel-AddResources-Button-Label')} />
    </FormElement>
  ) : (
    <FormElement css={{ paddingTop: 8 }}>
      <FormElement.Label label={translate('AppointmentPanel-FileUpload-Label')} labelFor={id} />
      <FileUpload
        id={id}
        accessibilityLabel={translate('AppointmentPanel-FileUpload-Label')}
        uploadLabel={translate('AppointmentPanel-FileUpload-Body')}
        fileLimitLabel={translate('AppointmentPanel-FileUpload-Limit-Label')}
        acceptedFilesLabel={translate('AppointmentPanel-FileUpload-AcceptedFiles-Label')}
        deleteFileLabel={translate('AppointmentPanel-FileUpload-DeleteFile-Label')}
        onFileSelected={onSelected}
        onRemoveFile={onRemove}
        files={files}
        multiple
        maxFiles={5}
        maxSize={maxFileSizeInBytes}
        acceptedFileTypes={acceptedFileTypes}
        hideDropZoneOnMaxFileLimit
        filesUploadedLabel={translate('AppointmentPanel-FileUpload-UploadedFile-Label', { numOfFiles: files.length })}
      />
    </FormElement>
  );
}

export default ApptFileUpload;
